import { uniqBy } from "lodash";
import { getGroupedResourceId, getResourceUserId } from "./groupedResourceId";

export const GROUP_BY_FIELDS = ["job", "location", "level1", "level2", "level3", "level4"];

const NO_GROUP_KEY = "no-group";

const isActive = (item: any) => item?.status === "active";

const renderGroupName = (item: any) => `${item.code} - ${item.description}`;

// Ids an item can be matched by: user jobs/locations carry the job/location id in `jobId`/`locationId`
const getItemIds = (item: any, field: string): string[] => {
    if (!item) return [];
    if (field === "job") return [item.jobId, item.id].filter(Boolean);
    if (field === "location") return [item.locationId, item.id].filter(Boolean);
    return [item.id].filter(Boolean);
};

// Every active job/location of the user (no duplicates), or the user's level for level grouping
const getResourceGroupItems = (resource: any, field: string) => {
    switch (field) {
        case "job":
            return uniqBy((resource.jobs || []).filter(isActive), "jobId")
                .map((job: any) => ({ key: job.jobId, value: job }));
        case "location":
            return uniqBy((resource.locations || []).filter(isActive), "locationId")
                .map((location: any) => ({ key: location.locationId, value: location }));
        default: {
            const level = resource[field];
            return level?.id ? [{ key: level.id, value: level }] : [];
        }
    }
};

export const getGroupedResources = (resources: any[], viewBy?: string) => {
    if (!viewBy || !GROUP_BY_FIELDS.includes(viewBy)) return resources;

    const groupMap = new Map<string, any>();

    const addToGroup = (resource: any, key: string, value: any) => {
        if (!groupMap.has(key)) {
            groupMap.set(key, {
                header: {
                    id: `group_header_${key}`,
                    originalId: key,
                    name: value ? renderGroupName(value) : "No Group",
                    type: "view-by",
                    isHeader: true
                },
                resources: []
            });
        }

        groupMap.get(key).resources.push({
            ...resource,
            id: getGroupedResourceId(resource.id, key),
            userId: resource.id,
            viewBy: key,
            originalId: key,
            groupBy: { field: viewBy, value }
        });
    };

    for (const resource of resources) {
        const items = getResourceGroupItems(resource, viewBy);
        if (items.length === 0) {
            addToGroup(resource, NO_GROUP_KEY, null);
        } else {
            items.forEach(({ key, value }) => addToGroup(resource, key, value));
        }
    }

    const result: any[] = [];

    for (const [, group] of groupMap) {
        result.push(group.header);
        result.push(...group.resources);
    }

    return result;
};

const isEventInGroup = (event: any, groupBy: any) => {
    const eventValue = event?.sections?.[0]?.[groupBy.field];
    if (!groupBy.value) return !eventValue;
    const groupIds = getItemIds(groupBy.value, groupBy.field);
    return getItemIds(eventValue, groupBy.field).some((id) => groupIds.includes(id));
};

// Points each event to the row of its user that matches the event's job/location/level.
// Events without a matching row keep their row when it is valid, otherwise go to the user's first row.
export const assignEventsToGroupedResources = (events: any[], resources: any[]) => {
    const rowsByUser = new Map<string, any[]>();
    resources?.forEach((resource: any) => {
        if (resource?.isHeader || !resource?.groupBy) return;
        const rows = rowsByUser.get(resource.userId) || [];
        rows.push(resource);
        rowsByUser.set(resource.userId, rows);
    });

    if (!events || rowsByUser.size === 0) return events;

    let changed = false;
    const next = events.map((event: any) => {
        const rows = rowsByUser.get(getResourceUserId(event?.resourceId));
        if (!rows) return event;

        const row = (event?.sections?.length
            ? rows.find((item) => isEventInGroup(event, item.groupBy))
            : undefined)
            ?? rows.find((item) => item.id === event.resourceId)
            ?? rows[0];

        if (row.id === event.resourceId) return event;
        changed = true;
        return { ...event, resourceId: row.id };
    });

    return changed ? next : events;
};

// The field the row is grouped by and its value, which the shift must keep
export const getResourceGroupValues = (resource: any): Record<string, any> => {
    const groupBy = resource?.groupBy;
    return groupBy?.value ? { [groupBy.field]: groupBy.value } : {};
};
